import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { Compass, Lock, BookOpen, GraduationCap, FileSpreadsheet, Megaphone, MoreHorizontal } from 'lucide-react';
import { AppShell } from './shell/AppShell';
import { useAuthStore, type Role } from './stores/authStore';
import { PageSkeleton } from './shell/PageSkeleton';
import { EmptyState } from './shell/EmptyState';

// ── Lazy page imports (code-split to stay within 200 kB gzip budget) ──────
// LoginPage stays eager — it's the critical entry point for all users.
import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';

function lazyRoute(importFn: () => Promise<{ default: ComponentType<any> }>) {
  const Component = lazy(importFn);
  const displayName = importFn.toString().match(/['"]([^'"]+\.(?:tsx|ts))['"]/)?.[1] ?? 'LazyPage';

  function LazyPageWrapper() {
    return (
      <Suspense fallback={<PageSkeleton ariaLabel="Loading page" />}>
        <Component />
      </Suspense>
    );
  }
  LazyPageWrapper.displayName = `LazyRoute(${displayName})`;
  return LazyPageWrapper;
}

const SignupPage = lazyRoute(() => import('./pages/SignupPage').then((m) => ({ default: m.SignupPage })));
const DemoModePage = lazyRoute(() => import('./pages/DemoModePage').then((m) => ({ default: m.DemoModePage })));
const PricingPage = lazyRoute(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const CheckoutPage = lazyRoute(() => import('./pages/CheckoutPage').then((m) => ({ default: m.CheckoutPage })));
const CheckoutSuccessPage = lazyRoute(() => import('./pages/CheckoutResultPages').then((m) => ({ default: m.CheckoutSuccessPage })));
const CheckoutFailedPage = lazyRoute(() => import('./pages/CheckoutResultPages').then((m) => ({ default: m.CheckoutFailedPage })));
const SettingsPage = lazyRoute(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const DashboardPage = lazyRoute(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const AttendancePage = lazyRoute(() => import('./pages/AttendancePage').then((m) => ({ default: m.AttendancePage })));
const CollectPage = lazyRoute(() => import('./pages/CollectPage').then((m) => ({ default: m.CollectPage })));
const MarksPage = lazyRoute(() => import('./pages/MarksPage').then((m) => ({ default: m.MarksPage })));
const ParentAttendancePage = lazyRoute(() => import('./pages/ParentAttendancePage').then((m) => ({ default: m.ParentAttendancePage })));
const ParentFeesPage = lazyRoute(() => import('./pages/ParentFeesPage').then((m) => ({ default: m.ParentFeesPage })));
const ResultsPage = lazyRoute(() => import('./pages/ResultsPage').then((m) => ({ default: m.ResultsPage })));
const NoticesPage = lazyRoute(() => import('./pages/NoticesPage').then((m) => ({ default: m.NoticesPage })));
const PeoplePage = lazyRoute(() => import('./pages/PeoplePage').then((m) => ({ default: m.PeoplePage })));
const StudentProfilePage = lazyRoute(() => import('./pages/StudentProfilePage').then((m) => ({ default: m.StudentProfilePage })));
const GuardiansPage = lazyRoute(() => import('./pages/GuardiansPage').then((m) => ({ default: m.GuardiansPage })));
const GuardianPortalPage = lazyRoute(() => import('./pages/GuardianPortalPage').then((m) => ({ default: m.GuardianPortalPage })));
const ClassesSectionsPage = lazyRoute(() => import('./pages/ClassesSectionsPage').then((m) => ({ default: m.ClassesSectionsPage })));
const SubjectsPage = lazyRoute(() => import('./pages/SubjectsPage').then((m) => ({ default: m.SubjectsPage })));
const CalendarPage = lazyRoute(() => import('./pages/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const TimetablePage = lazyRoute(() => import('./pages/TimetablePage').then((m) => ({ default: m.TimetablePage })));
const ExamTermsPage = lazyRoute(() => import('./pages/ExamTermsPage').then((m) => ({ default: m.ExamTermsPage })));
const ReportCardGenerationPage = lazyRoute(() => import('./pages/ReportCardGenerationPage').then((m) => ({ default: m.ReportCardGenerationPage })));
const FeeStructureBuilderPage = lazyRoute(() => import('./pages/FeeStructureBuilderPage').then((m) => ({ default: m.FeeStructureBuilderPage })));
const InvoiceRunPage = lazyRoute(() => import('./pages/InvoiceRunPage').then((m) => ({ default: m.InvoiceRunPage })));
const RefundsAdjustmentsPage = lazyRoute(() => import('./pages/RefundsAdjustmentsPage').then((m) => ({ default: m.RefundsAdjustmentsPage })));
const PaymentsReconciliationPage = lazyRoute(() => import('./pages/PaymentsReconciliationPage').then((m) => ({ default: m.PaymentsReconciliationPage })));
const ReportsPage = lazyRoute(() => import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const AccountantDashboard = lazyRoute(() => import('./pages/AccountantDashboard').then((m) => ({ default: m.AccountantDashboard })));
const ApplicantTrackingPage = lazyRoute(() => import('./pages/ApplicantTrackingPage').then((m) => ({ default: m.ApplicantTrackingPage })));
const AdmissionFormPage = lazyRoute(() => import('./pages/AdmissionFormPage').then((m) => ({ default: m.AdmissionFormPage })));

// ComingSoon is tiny — kept eager since it's used inline in many RouteSwitch branches.
import { ComingSoon } from './pages/ComingSoon';

// ── Eager route helpers ───────────────────────────────────────────────────
// Generic landing for roles that have no bespoke modules yet (the fallback nav
// target from roleNav). Honest "nothing configured" rather than a dead end or
// fake content (MYSKUX-001).
function WorkspaceLanding() {
  return (
    <EmptyState
      icon={<Compass size={48} />}
      title="Your workspace"
      description="No modules are configured for your role in this school yet. Contact your school admin to request access."
    />
  );
}

function NotAuthorized() {
  return (
    <div className="empty-state">
      <div className="empty-state-icon" aria-hidden="true"><Lock size={48} /></div>
      <h3>Not authorized</h3>
      <p>You don't have access to this page. Contact your school admin if you need access.</p>
    </div>
  );
}

function RequireRole({ roles, children }: { roles: Role[]; children: React.ReactNode }) {
  const session = useAuthStore((state) => state.session);
  if (!session) return <Navigate to="/login" replace />;
  if (!roles.includes(session.activeRole)) return <NotAuthorized />;
  return <>{children}</>;
}

// A single path can serve different pages per role (e.g. /attendance, /fees).
// Branch on the active role so one route is registered per path (React Router
// only matches the first route for a given path, so duplicate paths would
// otherwise shadow each other and block legitimate roles).
function RoleSwitch({ branches }: { branches: Partial<Record<Role, React.ReactNode>> }) {
  const session = useAuthStore((state) => state.session);
  if (!session) return <Navigate to="/login" replace />;
  const element = branches[session.activeRole];
  if (!element) return <NotAuthorized />;
  return <>{element}</>;
}

export const routeConfig: RouteObject[] = [
  { path: '/', element: <LandingPage /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/demo', element: <DemoModePage /> },
  { path: '/pricing', element: <PricingPage /> },
  { path: '/checkout', element: <CheckoutPage /> },
  { path: '/checkout/success', element: <CheckoutSuccessPage /> },
  { path: '/checkout/failed', element: <CheckoutFailedPage /> },
  { path: '/apply', element: <AdmissionFormPage /> },
  {
    element: <AppShell />,
    children: [
      { path: 'dashboard', element: <RequireRole roles={['SchoolAdmin']}><DashboardPage /></RequireRole> },
      { path: 'today', element: <RequireRole roles={['Teacher', 'Student']}><DashboardPage /></RequireRole> },
      { path: 'home', element: <RequireRole roles={['Parent']}><DashboardPage /></RequireRole> },
      { path: 'collect', element: <RequireRole roles={['Accountant']}><CollectPage /></RequireRole> },
      { path: 'finance', element: <RequireRole roles={['Accountant']}><AccountantDashboard /></RequireRole> },
      {
        path: 'attendance',
        element: (
          <RoleSwitch branches={{ Teacher: <AttendancePage />, Parent: <ParentAttendancePage /> }} />
        ),
      },
      { path: 'marks', element: <RequireRole roles={['Teacher', 'ExamCoordinator']}><MarksPage /></RequireRole> },
      {
        path: 'fees',
        element: (
          <RoleSwitch
            branches={{
              Parent: <ParentFeesPage />,
              SchoolAdmin: <FeeStructureBuilderPage />,
              Accountant: <FeeStructureBuilderPage />,
            }}
          />
        ),
      },
      { path: 'results', element: <RequireRole roles={['Parent', 'Student']}><ResultsPage /></RequireRole> },
      { path: 'notices', element: <RequireRole roles={['Parent']}><NoticesPage /></RequireRole> },
      { path: 'people', element: <RequireRole roles={['SchoolAdmin']}><PeoplePage /></RequireRole> },
      { path: 'people/:id', element: <RequireRole roles={['SchoolAdmin']}><StudentProfilePage /></RequireRole> },
      { path: 'guardians', element: <RequireRole roles={['SchoolAdmin', 'FrontDesk']}><GuardiansPage /></RequireRole> },
      { path: 'portal/:guardianId', element: <RequireRole roles={['SchoolAdmin', 'FrontDesk']}><GuardianPortalPage /></RequireRole> },
      { path: 'admissions', element: <RequireRole roles={['SchoolAdmin', 'FrontDesk']}><ApplicantTrackingPage /></RequireRole> },
      { path: 'academics', element: <Navigate to="/academics/classes" replace /> },
      { path: 'academics/classes', element: <RequireRole roles={['SchoolAdmin', 'ExamCoordinator']}><ClassesSectionsPage /></RequireRole> },
      { path: 'academics/subjects', element: <RequireRole roles={['SchoolAdmin', 'ExamCoordinator']}><SubjectsPage /></RequireRole> },
      { path: 'calendar', element: <RequireRole roles={['SchoolAdmin', 'Teacher', 'Parent']}><CalendarPage /></RequireRole> },
      { path: 'exam-terms', element: <RequireRole roles={['ExamCoordinator', 'SchoolAdmin']}><ExamTermsPage /></RequireRole> },
      { path: 'report-cards', element: <RequireRole roles={['ExamCoordinator', 'SchoolAdmin']}><ReportCardGenerationPage /></RequireRole> },
      { path: 'comms', element: <RequireRole roles={['SchoolAdmin']}><ComingSoon icon={<Megaphone size={32} />} title="Communication" description="Send bulk notices, alerts, and messages to parents and staff." /></RequireRole> },
      { path: 'settings', element: <RequireRole roles={['SchoolAdmin']}><SettingsPage /></RequireRole> },
      { path: 'homework', element: <RequireRole roles={['Teacher', 'Student']}><ComingSoon icon={<BookOpen size={32} />} title="Homework" description="Assign, submit, and grade homework." /></RequireRole> },
      { path: 'timetable', element: <RequireRole roles={['Student', 'Teacher', 'SchoolAdmin', 'ExamCoordinator']}><TimetablePage /></RequireRole> },
      { path: 'materials', element: <RequireRole roles={['Student']}><ComingSoon icon={<FileSpreadsheet size={32} />} title="Materials" description="Access study materials and resources." /></RequireRole> },
      { path: 'invoices', element: <RequireRole roles={['Accountant']}><InvoiceRunPage /></RequireRole> },
      { path: 'reports', element: <RequireRole roles={['Accountant', 'SchoolAdmin']}><ReportsPage /></RequireRole> },
      { path: 'reconciliation', element: <RequireRole roles={['Accountant', 'SchoolAdmin']}><PaymentsReconciliationPage /></RequireRole> },
      { path: 'refunds', element: <RequireRole roles={['Accountant', 'SchoolAdmin']}><RefundsAdjustmentsPage /></RequireRole> },
      { path: 'more', element: <RequireRole roles={['Teacher', 'Accountant']}><ComingSoon icon={<MoreHorizontal size={32} />} title="More" description="Additional features and settings." /></RequireRole> },
      // Fallback landing for roles without a bespoke menu; reachable by any
      // authenticated role so navForRole's fallback never dead-ends (MYSKUX-001).
      { path: 'workspace', element: <WorkspaceLanding /> },
    ],
  },
];
